import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatCurrency, getMonthName, getCurrentYear } from '../lib/utils';
import { FileText, Trash2 } from 'lucide-react';
import { ExportButtons } from '../components/ExportButtons';

export function RecapDepensesPage() {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [year, setYear] = useState(getCurrentYear());
  const [filterCat, setFilterCat] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [year]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/recap-depenses', { params: { annee: year } });
      setData(res.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecap = async (mois, categorie) => {
    try {
      await api.delete('/recap-depenses', { params: { annee: year, mois, categorie } });
      toast.success(`${categorie} (${getMonthName(mois)}) supprimé`);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  // Group by month
  const uniqueCategories = [...new Set(data.map(d => d.categorie || 'Non catégorisé'))].sort();
  const filteredData = filterCat === 'all' ? data : data.filter(d => (d.categorie || 'Non catégorisé') === filterCat);

  const byMonth = filteredData.reduce((acc, item) => {
    if (!acc[item.mois]) acc[item.mois] = [];
    acc[item.mois].push(item);
    return acc;
  }, {});

  const totalPrevues = filteredData.reduce((sum, d) => sum + d.prevues, 0);
  const totalReelles = filteredData.reduce((sum, d) => sum + d.reelles, 0);
  const globalProgress = totalPrevues > 0 ? (totalReelles / totalPrevues) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title">Récap Dépenses</h1>
          <p className="text-muted-foreground mt-1">Vue consolidée des dépenses par mois et catégorie</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-[200px]" data-testid="filter-categorie-recap">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {uniqueCategories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ExportButtons api={api} moduleKey="recap_depenses" year={year} />
          {filterCat !== 'all' && <Button variant="ghost" size="sm" onClick={() => setFilterCat('all')}>Effacer</Button>}
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Progression globale</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4">
              <div>
                <p className="text-3xl font-bold tabular-nums">{globalProgress.toFixed(1)}%</p>
                <p className="text-sm text-muted-foreground">du budget consommé</p>
              </div>
              <div className="flex-1">
                <Progress value={Math.min(globalProgress, 100)} className="h-3" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Total Prévu</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">{formatCurrency(totalPrevues)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Total Réel</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums text-red-600">{formatCurrency(totalReelles)}</p>
          </CardContent>
        </Card>
      </div>

      {/* By Month */}
      {Object.entries(byMonth).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([mois, items]) => {
        const monthPrev = items.reduce((s, i) => s + i.prevues, 0);
        const monthReel = items.reduce((s, i) => s + i.reelles, 0);
        const monthProgress = monthPrev > 0 ? (monthReel / monthPrev) * 100 : 0;

        return (
          <Card key={mois}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {getMonthName(parseInt(mois))} {year}
                </CardTitle>
                <div className="flex items-center gap-4 text-sm">
                  <span>Prévu: <strong className="tabular-nums">{formatCurrency(monthPrev)}</strong></span>
                  <span>Réel: <strong className="tabular-nums text-red-600">{formatCurrency(monthReel)}</strong></span>
                  <span className={`font-medium ${monthProgress > 100 ? 'text-red-600' : 'text-green-600'}`}>
                    {monthProgress.toFixed(0)}%
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Catégorie</TableHead>
                    <TableHead className="text-right">Prévu</TableHead>
                    <TableHead className="text-right">Réel</TableHead>
                    <TableHead className="text-right">Reste</TableHead>
                    <TableHead className="w-[150px]">Progression</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{item.categorie || 'Non catégorisé'}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(item.prevues)}</TableCell>
                      <TableCell className="text-right tabular-nums text-red-600">{formatCurrency(item.reelles)}</TableCell>
                      <TableCell className={`text-right tabular-nums ${item.reste_a_payer < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(item.reste_a_payer)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={Math.min(item.pct_payer, 100)} className="h-2 flex-1" />
                          <span className={`text-xs tabular-nums ${item.pct_payer > 100 ? 'text-red-600' : ''}`}>
                            {item.pct_payer.toFixed(0)}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Supprimer ?</AlertDialogTitle><AlertDialogDescription>Toutes les dépenses "{item.categorie}" de {getMonthName(parseInt(mois))} {year} seront supprimées.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteRecap(parseInt(mois), item.categorie)}>Supprimer</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}

      {data.length === 0 && !loading && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Aucune donnée pour cette année. Ajoutez des dépenses prévisionnelles et importez des données Linxo.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
